import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloseIcon from "@mui/icons-material/Close";
import { TOKENS } from "./styled";
import {
  processForUpload,
  TooLargeError,
  UnsupportedFormatError,
  ProcessedImage,
} from "./imagePipeline";

const MAX_PHOTOS = 5;

export type ArticlePhoto = ProcessedImage & { localId: string };

type Props = {
  photos: ArticlePhoto[];
  onChange: (next: ArticlePhoto[]) => void;
  disabled?: boolean;
  extraActions?: React.ReactNode;
};

const ArticlePhotoStrip: React.FC<Props> = ({
  photos,
  onChange,
  disabled,
  extraActions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const remaining = Math.max(0, MAX_PHOTOS - photos.length);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    if (files.length > remaining) {
      setError(`5 photos max par article`);
    }
    const accepted = files.slice(0, remaining);
    if (accepted.length === 0) return;

    setBusy((n) => n + accepted.length);
    const results = await Promise.all(
      accepted.map(async (file): Promise<ArticlePhoto | null> => {
        try {
          const processed = await processForUpload(file);
          return {
            ...processed,
            localId:
              typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random()}`,
          };
        } catch (err) {
          if (err instanceof TooLargeError) {
            setError("Fichier trop volumineux (25 Mo max).");
          } else if (err instanceof UnsupportedFormatError) {
            setError("Format d'image non supporté.");
          } else {
            setError("Échec du traitement de l'image.");
          }
          return null;
        }
      })
    );
    setBusy((n) => Math.max(0, n - accepted.length));

    const added = results.filter((r): r is ArticlePhoto => r !== null);
    if (added.length > 0) {
      onChange([...photos, ...added]);
    }
  };

  const removePhoto = (localId: string) => {
    const target = photos.find((p) => p.localId === localId);
    if (target?.thumbUrl) {
      try {
        URL.revokeObjectURL(target.thumbUrl);
      } catch (e) {
        /* noop */
      }
    }
    onChange(photos.filter((p) => p.localId !== localId));
  };

  const triggerCamera = () => cameraRef.current?.click();
  const triggerGallery = () => galleryRef.current?.click();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          marginBottom: photos.length > 0 || busy > 0 ? "0.5rem" : 0,
        }}
      >
        {photos.map((p) => (
          <Box
            key={p.localId}
            sx={{
              position: "relative",
              width: 64,
              height: 64,
              borderRadius: 8,
              overflow: "hidden",
              border: `1px solid ${TOKENS.GRAY_300}`,
              background: TOKENS.GRAY_100,
            }}
          >
            <img
              src={p.thumbUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <IconButton
              size="small"
              onClick={() => removePhoto(p.localId)}
              disabled={disabled}
              aria-label="Supprimer la photo"
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                padding: "2px",
                "&:hover": { background: "rgba(0,0,0,0.75)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        {Array.from({ length: busy }).map((_, i) => (
          <Box
            key={`busy-${i}`}
            sx={{
              width: 64,
              height: 64,
              borderRadius: 8,
              border: `1px dashed ${TOKENS.GRAY_300}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: TOKENS.GRAY_100,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        {isMobile ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAltIcon />}
              onClick={triggerCamera}
              disabled={disabled || remaining === 0}
              sx={{ textTransform: "none" }}
            >
              Prendre une photo
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PhotoLibraryIcon />}
              onClick={triggerGallery}
              disabled={disabled || remaining === 0}
              sx={{ textTransform: "none" }}
            >
              Galerie
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoLibraryIcon />}
            onClick={triggerGallery}
            disabled={disabled || remaining === 0}
            sx={{ textTransform: "none" }}
          >
            Ajouter des photos
          </Button>
        )}
        {extraActions}
      </Box>

      {photos.length === 0 && busy === 0 && (
        <Typography
          sx={{
            color: TOKENS.GRAY_500,
            fontSize: "0.72rem",
            mt: 0.5,
            fontStyle: "italic",
          }}
        >
          Une photo aide nos techniciens à préparer l&apos;intervention.
        </Typography>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.currentTarget.value = "";
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.currentTarget.value = "";
        }}
      />

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setError(null)}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticlePhotoStrip;
